import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon/Icon';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import styles from './KebabMenu.module.css';

export interface KebabMenuItem {
  label: string;
  icon?: string;
  onClick?: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  /** When provided, clicking this item opens the subPanel instead of calling onClick */
  subPanel?: React.ReactNode;
}

interface KebabMenuProps {
  items: KebabMenuItem[];
  className?: string;
  triggerClassName?: string;
  portal?: boolean;
}

const DROPDOWN_WIDTH = 222;
const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 8;

const KebabMenu: React.FC<KebabMenuProps> = ({
  items,
  className = '',
  triggerClassName = '',
  portal = false,
}) => {
  const [open, setOpen] = useState(false);
  const [hoveredSubPanel, setHoveredSubPanel] = useState<number | null>(null);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setHoveredSubPanel(null);
  }, []);
  useEscapeToClose(closeMenu, open);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (ref.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }

      closeMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [closeMenu, open]);

  useLayoutEffect(() => {
    if (!open || !portal || !ref.current) {
      return undefined;
    }

    const updatePosition = () => {
      const triggerRect = ref.current?.getBoundingClientRect();

      if (!triggerRect) {
        return;
      }

      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
      const nextLeft = Math.min(
        Math.max(triggerRect.right - DROPDOWN_WIDTH, VIEWPORT_MARGIN),
        window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN,
      );
      const opensAbove = triggerRect.bottom + DROPDOWN_GAP + dropdownHeight > window.innerHeight - VIEWPORT_MARGIN;
      const nextTop = opensAbove
        ? Math.max(VIEWPORT_MARGIN, triggerRect.top - DROPDOWN_GAP - dropdownHeight)
        : triggerRect.bottom + DROPDOWN_GAP;

      setPortalPosition({ top: nextTop, left: nextLeft });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, portal]);

  const handleItemMouseEnter = (item: KebabMenuItem, i: number) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    if (item.subPanel) {
      hoverTimerRef.current = setTimeout(() => setHoveredSubPanel(i), 250);
    } else {
      setHoveredSubPanel(null);
    }
  };

  const handleItemMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  };

  const handleItemClick = (item: KebabMenuItem) => {
    if (item.disabled || item.subPanel) {
      return;
    }

    item.onClick?.();
    closeMenu();
  };

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      className={`${styles.dropdown}${portal ? ` ${styles.dropdownPortal}` : ''}`}
      role="menu"
      style={portal ? { top: portalPosition.top, left: portalPosition.left } : undefined}
    >
      {items.map((item, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          className={`${styles.item} ${item.variant === 'danger' ? styles.danger : ''}`}
          onClick={() => handleItemClick(item)}
          onMouseEnter={() => handleItemMouseEnter(item, i)}
          onMouseLeave={handleItemMouseLeave}
          disabled={item.disabled}
        >
          <span className={styles.itemContent}>
            {item.icon ? <Icon name={item.icon} size={18} className={styles.itemIcon} /> : null}
            <span>{item.label}</span>
          </span>
          {item.subPanel ? <Icon name="chevron_right" size={16} /> : null}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div
      className={`${styles.container} ${className}`.trim()}
      ref={ref}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`${styles.trigger} ${triggerClassName}`.trim()}
        onClick={() => {
          setOpen((currentOpen) => !currentOpen);
          setHoveredSubPanel(null);
        }}
        aria-label="Mais opções"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Icon name="more_vert" size={18} style={{ fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} />
      </button>

      {portal && dropdown ? createPortal(dropdown, document.body) : dropdown}

      {open && !portal && hoveredSubPanel !== null && items[hoveredSubPanel].subPanel && (
        <div
          className={styles.subPanelWrapper}
          onMouseEnter={() => {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
          }}
          onMouseLeave={() => setHoveredSubPanel(null)}
        >
          {items[hoveredSubPanel].subPanel}
        </div>
      )}
    </div>
  );
};

export default KebabMenu;
